import React, { createContext, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { X } from 'lucide-react';

const AuthGateContext = createContext(null);

export const AuthGateProvider = ({ children }) => {
  const [show, setShow] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  const requireAuth = (path = null, callback = null) => {
    setPendingPath({ path, callback });
    setShow(true);
  };

  const close = () => { setShow(false); setPendingPath(null); };

  return (
    <AuthGateContext.Provider value={{ requireAuth }}>
      {children}
      {show && <AuthGateModal onClose={close} pending={pendingPath} />}
    </AuthGateContext.Provider>
  );
};

export const useAuthGate = () => {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error('useAuthGate must be used within AuthGateProvider');
  return ctx;
};

const AuthGateModal = ({ onClose, pending }) => {
  const navigate = useNavigate();

  const goTo = (path) => {
    onClose();
    navigate(path, { state: { from: pending?.path } });
  };

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center animate-in fade-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        {/* Logo */}
        <div className="flex items-center justify-center gap-0 mb-2">
          <span className="text-2xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
          <img
            src="/rentalx-x-new.svg"
            alt="X"
            style={{ height: '1.7rem', width: 'auto', marginLeft: '0.06em', marginBottom: '0.02em', verticalAlign: 'middle' }}
          />
        </div>

        {/* Lock icon */}
        <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">🔑</span>
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to continue</h2>
        <p className="text-gray-500 text-sm mb-6">
          Create a free account to browse properties, connect with landlords, and find your perfect student home.
        </p>

        <div className="space-y-3">
          <button
            onClick={() => goTo('/login')}
            className="w-full bg-[#094f6f] text-white py-3 rounded-xl font-semibold hover:bg-[#094f6f]/90 transition-colors"
            data-testid="authgate-login-btn"
          >
            Sign In
          </button>
          <button
            onClick={() => goTo('/register/student')}
            className="w-full border-2 border-[#094f6f] text-[#094f6f] py-3 rounded-xl font-semibold hover:bg-sky-50 transition-colors"
            data-testid="authgate-register-student-btn"
          >
            Create Student Account
          </button>
          <button
            onClick={() => goTo('/register/landlord')}
            className="w-full border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm hover:bg-gray-50 transition-colors"
          >
            I'm a Property Manager / Landlord
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-4">
          100% free for students · No credit card required
        </p>
      </div>
    </div>
  );
};

// ProtectedLink — drops in as a replacement for <Link>
export const ProtectedLink = ({ to, children, className, onClick, ...props }) => {
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();

  const handleClick = (e) => {
    if (!user) {
      e.preventDefault();
      requireAuth(to);
    } else if (onClick) {
      onClick(e);
    }
  };

  return (
    <Link to={to} className={className} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
};
